import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Clock, User, Sparkle, Warning, CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { fmtYmdTime } from "@/lib/dateFormat";

export default function InspectionHistory() {
    const { features } = useAuth();
    const [list, setList] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);
    const [analyzing, setAnalyzing] = useState(false);

    useEffect(() => {
        api.get("/inspections").then((r) => { setList(r.data); setLoading(false); });
    }, []);

    const runAiAnalysis = async () => {
        if (!selected) return;
        setAnalyzing(true);
        try {
            const { data } = await api.post(`/ai-vision/inspections/${selected.id}/analyze`);
            toast.success(`AI解析完了: ${data.analyzed_count}件解析 / ${data.skipped_count}件スキップ`);
            // Refresh detail
            const { data: refreshed } = await api.get(`/inspections/${selected.id}`);
            setSelected(refreshed);
            // Also refresh list to pick up ai_overall_status
            const { data: refList } = await api.get("/inspections");
            setList(refList);
        } catch (e) {
            toast.error("AI解析に失敗しました: " + (e?.response?.data?.detail || e.message));
        } finally {
            setAnalyzing(false);
        }
    };

    return (
        <div className="space-y-5" data-testid="inspection-history-page">
            <div>
                <p className="prc-label-overline">点検履歴</p>
                <h1 className="text-[1.685rem] font-bold tracking-tight">使用前点検の全履歴</h1>
            </div>

            <div className="grid lg:grid-cols-5 gap-4">
                <div className="prc-card lg:col-span-2 overflow-hidden">
                    <div className="p-4 border-b border-[var(--prc-border)]">
                        <p className="prc-label-overline">一覧（新しい順）</p>
                    </div>
                    {loading ? <p className="p-4 text-sm">読込中…</p> : (
                        <ul className="divide-y divide-[var(--prc-border)] max-h-[600px] overflow-auto" data-testid="history-list">
                            {list.map((i) => (
                                <li key={i.id}>
                                    <button
                                        onClick={() => setSelected(i)}
                                        className={`w-full text-left p-4 hover:bg-[var(--prc-bg)] transition ${selected?.id === i.id ? "bg-[var(--prc-bg)]" : ""}`}
                                        data-testid={`history-item-${i.id}`}
                                    >
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <p className="font-mono font-semibold text-sm">{i.vehicle_plate}</p>
                                                <p className="text-xs text-[var(--prc-muted)] mt-0.5">{fmtYmdTime(i.started_at, String(i.started_at).substring(0, 16).replace("T", " "))}</p>
                                            </div>
                                            <span className={`prc-badge ${i.overall_status === "ng" ? "ng" : i.overall_status === "warn" ? "warn" : i.status === "completed" ? "ok" : "muted"}`}>
                                                {i.overall_status?.toUpperCase() || i.status}
                                            </span>
                                        </div>
                                        <p className="text-xs text-[var(--prc-muted)] mt-1">{i.inspector_name}</p>
                                    </button>
                                </li>
                            ))}
                            {list.length === 0 && <li className="p-4 text-sm text-[var(--prc-muted)]">履歴がありません</li>}
                        </ul>
                    )}
                </div>

                <div className="prc-card lg:col-span-3 p-5">
                    {!selected ? (
                        <p className="text-sm text-[var(--prc-muted)]" data-testid="history-empty">左の一覧から点検を選択してください</p>
                    ) : (
                        <div data-testid="history-detail">
                            <div className="flex justify-between items-start gap-3">
                                <div>
                                    <p className="prc-label-overline">点検詳細</p>
                                    <h2 className="text-2xl font-mono font-bold tracking-tight mt-1">{selected.vehicle_plate}</h2>
                                </div>
                                <button
                                    onClick={runAiAnalysis}
                                    disabled={analyzing}
                                    className="prc-btn-accent inline-flex items-center gap-2 shrink-0"
                                    data-testid="ai-analyze-btn"
                                    hidden={!features?.paid_ai_enabled}
                                >
                                    <Sparkle size={16} weight="fill" />
                                    {analyzing ? "解析中…" : "AI写真解析"}
                                </button>
                            </div>

                            {selected.ai_overall_status && (
                                <div className={`mt-3 p-3 border rounded-sm flex items-center gap-2 ${
                                    selected.ai_overall_status === "ng" ? "border-[var(--prc-red)] bg-[rgba(255,80,80,0.08)]" :
                                    selected.ai_overall_status === "warn" ? "border-[var(--prc-amber)] bg-[rgba(255,181,71,0.08)]" :
                                    "border-[var(--prc-green)] bg-[rgba(0,255,156,0.06)]"
                                }`} data-testid="ai-overall-status">
                                    {selected.ai_overall_status === "ok" ? <CheckCircle size={24} className="text-[var(--prc-green)]" weight="fill" /> : <Warning size={24} className={selected.ai_overall_status === "ng" ? "text-[var(--prc-red)]" : "text-[var(--prc-amber)]"} weight="fill" />}
                                    <span className="font-mono font-bold text-xs tracking-wider uppercase">
                                        AI総合判定: {selected.ai_overall_status}
                                    </span>
                                    {selected.ai_analyzed_at && (
                                        <span className="text-xs text-[var(--prc-muted)] ml-auto font-mono">
                                            {String(selected.ai_analyzed_at).substring(0, 19).replace("T", " ")}
                                        </span>
                                    )}
                                </div>
                            )}
                            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                                <div className="flex gap-2"><Clock size={16} className="text-[var(--prc-muted)]" /><span>{String(selected.started_at).substring(0, 19).replace("T", " ")}</span></div>
                                <div className="flex gap-2"><User size={16} className="text-[var(--prc-muted)]" /><span>{selected.inspector_name}</span></div>
                                {selected.location && <div className="flex gap-2 col-span-2"><MapPin size={16} className="text-[var(--prc-muted)]" /><span>{selected.location}</span></div>}
                                {selected.gps_lat && <div className="text-xs font-mono text-[var(--prc-muted)] col-span-2">GPS {selected.gps_lat?.toFixed(5)}, {selected.gps_lng?.toFixed(5)}</div>}
                                {selected.mileage != null && <div>走行距離: <span className="font-mono font-semibold">{selected.mileage.toLocaleString()} km</span></div>}
                            </div>

                            <div className="mt-5">
                                <p className="prc-label-overline mb-2">点検項目</p>
                                <div className="divide-y divide-[var(--prc-border)] border border-[var(--prc-border)] rounded-sm">
                                    {(selected.items || []).map((it, i) => (
                                        <div key={i} className="p-3 flex gap-3 items-start">
                                            <span className={`prc-badge ${it.status === "ok" ? "ok" : it.status === "warn" ? "warn" : "ng"} shrink-0`}>{it.status?.toUpperCase()}</span>
                                            <div className="flex-1">
                                                <p className="font-semibold text-sm">{it.label}</p>
                                                {it.comment && <p className="text-xs text-[var(--prc-muted)] mt-0.5">{it.comment}</p>}
                                                {it.photo_data_url && <img src={it.photo_data_url} alt="" className="mt-2 max-h-40 border border-[var(--prc-border)] rounded-sm" data-testid={`history-item-photo-${i}`} />}
                                                {it.ai_analysis && (
                                                    <div className={`mt-2 p-2.5 border rounded-sm text-xs ${
                                                        it.ai_analysis.severity === "ng" ? "border-[var(--prc-red)] bg-[rgba(255,80,80,0.06)]" :
                                                        it.ai_analysis.severity === "warn" ? "border-[var(--prc-amber)] bg-[rgba(255,181,71,0.06)]" :
                                                        "border-[var(--prc-green)] bg-[rgba(0,255,156,0.05)]"
                                                    }`} data-testid={`ai-result-${i}`}>
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <Sparkle size={16} weight="fill" className="text-[var(--prc-accent)]" />
                                                            <span className="font-mono font-bold tracking-wider uppercase text-[10px]">
                                                                AI判定: <span className={
                                                                    it.ai_analysis.severity === "ng" ? "text-[var(--prc-red)]" :
                                                                    it.ai_analysis.severity === "warn" ? "text-[var(--prc-amber)]" :
                                                                    "text-[var(--prc-green)]"
                                                                }>{it.ai_analysis.severity?.toUpperCase()}</span>
                                                            </span>
                                                            {it.ai_analysis.confidence > 0 && (
                                                                <span className="text-[10px] font-mono text-[var(--prc-muted)]">
                                                                    信頼度 {(it.ai_analysis.confidence * 100).toFixed(0)}%
                                                                </span>
                                                            )}
                                                        </div>
                                                        {it.ai_analysis.detected_issues?.length > 0 && (
                                                            <ul className="list-disc list-inside space-y-0.5 mb-1 text-[var(--prc-ink)]">
                                                                {it.ai_analysis.detected_issues.map((iss, j) => (
                                                                    <li key={j}>{iss}</li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                        {it.ai_analysis.ai_comment && (
                                                            <p className="text-[var(--prc-muted)] italic">💡 {it.ai_analysis.ai_comment}</p>
                                                        )}
                                                        {it.ai_analysis.recommended_action && (
                                                            <p className="mt-1 text-[var(--prc-ink-2)] font-semibold">
                                                                推奨対応: {it.ai_analysis.recommended_action}
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {selected.overall_comment && (
                                <div className="mt-4">
                                    <p className="prc-label-overline mb-1">総合コメント</p>
                                    <p className="text-sm">{selected.overall_comment}</p>
                                </div>
                            )}

                            {selected.signature_data_url && (
                                <div className="mt-4">
                                    <p className="prc-label-overline mb-1">署名</p>
                                    <img src={selected.signature_data_url} alt="signature" className="max-h-24 border border-[var(--prc-border)] bg-[var(--prc-surface-2)]" />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
